import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
} from '@mui/material';
import {
  Person,
  Business,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface Department {
  _id: string;
  name: string;
  isActive: boolean;
}

interface Office {
  _id: string;
  name: string;
  isActive: boolean;
}

const Onboarding: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [formData, setFormData] = useState({
    department: '',
    office: '',
  });
  const canChangeOrgData = user?.company?.settings?.allowEmployeeChangeCompanyOffice ?? true;

  useEffect(() => {
    if (canChangeOrgData) {
      fetchData();
    } else {
      // Si la empresa no permite cambios, no mostrar formulario
      setLoading(false);
    }
  }, [canChangeOrgData]);

  useEffect(() => {
    if (user && !canChangeOrgData) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, canChangeOrgData, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [departmentsData, officesData] = await Promise.all([
        api.getDepartments(),
        api.getOffices()
      ]);
      
      // Filtrar solo activos
      setDepartments(departmentsData.filter((d: Department) => d.isActive));
      setOffices(officesData.filter((o: Office) => o.isActive));
    } catch (err: any) {
      setError('Error cargando datos. Por favor recarga la página.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.department || !formData.office) {
      setError('Por favor selecciona tanto la sede como el departamento');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      await updateUser({
        department: formData.department,
        office: formData.office
      });
      
      // Redirigir al dashboard después de completar el onboarding
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error guardando información');
    } finally {
      setSaving(false);
    }
  };

  if (!canChangeOrgData) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Person sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Datos gestionados por tu empresa
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tu empresa administra tu sede y departamento. Serás redirigido al dashboard.
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Person sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Completa tu Perfil
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Por favor, indica la sede y departamento al que perteneces
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth required>
              <InputLabel>Sede</InputLabel>
              <Select
                value={formData.office}
                onChange={(e) => setFormData({ ...formData, office: e.target.value })}
                label="Sede"
                startAdornment={<LocationOn sx={{ ml: 1, mr: 1 }} />}
              >
                {offices.map((office) => (
                  <MenuItem key={office._id} value={office.name}>
                    {office.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Departamento</InputLabel>
              <Select
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                label="Departamento"
                startAdornment={<Business sx={{ ml: 1, mr: 1 }} />}
              >
                {departments.map((department) => (
                  <MenuItem key={department._id} value={department.name}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={saving || !formData.department || !formData.office}
              sx={{
                mt: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: 2,
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5a6fd8 30%, #6a4190 90%)',
                },
              }}
            >
              {saving ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Continuar'
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default Onboarding;

